import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoGrid from "@/components/feature/VideoGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/base/EmptyState";
import FilterPanel, {
  type CategoryFilters,
} from "@/pages/category/components/FilterPanel";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { categoryMeta, tagsByType } from "@/mocks/videos";
import { filterVideos, paginate } from "@/utils/videoFilters";

const PAGE_SIZE = 8;

const defaultFilters: CategoryFilters = {
  region: "全部",
  year: "全部",
  sort: "latest",
};

export default function CategoryPage() {
  const { type = "" } = useParams();
  const meta = categoryMeta.find((c) => c.key === type);

  const [filters, setFilters] = useState<CategoryFilters>(defaultFilters);
  const [tag, setTag] = useState("全部");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useDocumentTitle(
    meta ? `${meta.name} - 99视频高清视频平台` : "分类 - 99视频",
    meta
      ? `99视频${meta.name}频道，${meta.tagline}，涵盖多种类型与地区，支持按地区、年份、排序快速筛选想看的内容。`
      : "99视频分类频道"
  );

  useEffect(() => {
    setFilters(defaultFilters);
    setTag("全部");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [filters, tag]);

  const filtered = useMemo(
    () => filterVideos({ type, ...filters, tag }),
    [type, filters, tag]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = paginate(filtered, page, PAGE_SIZE);
  const tags = tagsByType[type] || ["全部"];

  if (!meta) {
    return (
      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-20">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="您访问的分类页面可能已被调整，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const handleFilterChange = (next: Partial<CategoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setTag("全部");
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6 md:py-10">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: meta.name }]}
          />
          <div className="mt-4 flex items-start gap-4">
            <span className="hidden sm:flex w-12 h-12 rounded-lg bg-primary-100 text-primary-700 items-center justify-center text-2xl shrink-0">
              <i className={meta.icon}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {meta.name}频道
              </h1>
              <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
                {meta.tagline} · {meta.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                tag === t
                  ? "bg-accent-500 text-accent-950"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-[260px] shrink-0">
            <div className="sticky top-24 rounded-lg bg-background-50 border border-background-200 p-5">
              <FilterPanel
                filters={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
              />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部{meta.name}资源
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-equalizer-line"></i>
                筛选
              </button>
            </div>

            {pageItems.length === 0 ? (
              <div className="rounded-lg bg-background-50 border border-background-200">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="暂无符合条件的影视资源"
                  description="没有找到符合当前筛选条件的作品，试试调整或重置筛选条件。"
                />
                <div className="pb-12 -mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选条件
                  </button>
                </div>
              </div>
            ) : (
              <VideoGrid videos={pageItems} />
            )}

            {pageItems.length > 0 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[70]">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">
                筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-600 text-xl cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full h-11 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors cursor-pointer"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}